import React from 'react';
import { Link } from 'react-router-dom';
import LMSHeader from '../components/LMSHeader';
import contactBg from "../assets/drill.jpg";
import aboutBg from "../assets/images/bg-hero-desktop-two.jpg";


const AboutPage = () => {

  const achievements = [
    {
      number: '50,000+',
      label: 'Students Enrolled'
    },
    {
      number: '500+',
      label: 'Courses Available'
    },
    {
      number: '200+',
      label: 'Expert Instructors'
    },
    {
      number: '95%',
      label: 'Student Satisfaction'
    },
  ];

  const whyChooseUs = [
    {
      icon: '🎯',
      title: 'Industry-Relevant Curriculum',
      description: 'Our courses are designed by industry experts to ensure you learn the most in-demand skills.'
    },
    {
      icon: '👨‍🏫',
      title: 'Expert Instructors',
      description: 'Learn from professionals who have real-world experience in their fields.'
    },
    {
      icon: '📱',
      title: 'Learn Anywhere',
      description: 'Access courses on any device, anytime, with our mobile-friendly platform.'
    },
    {
      icon: '💰',
      title: 'Affordable Pricing',
      description: 'High-quality education at a fraction of the cost of traditional programs.'
    },
    {
      icon: '🤝',
      title: 'Community Support',
      description: 'Connect with instructors and fellow students through our discussion forums.'
    },
    {
      icon: '🏆',
      title: 'Career Advancement',
      description: 'Our certificates are recognized by employers and can help you advance your career.'
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <LMSHeader />

      <main className="flex-1">
        {/* Hero Section */}
        <section
          className="relative bg-gradient-to-r from-primary-500/90 to-primary-600/90 text-white py-20 bg-blend-overlay"
          style={{
            backgroundImage: `url(${contactBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center center",
            backgroundRepeat: "no-repeat",
          }}
        >
          {/* Optional overlay for better text contrast */}
          <div className="absolute inset-0 bg-black/40 z-0"></div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">About BoleumLearn</h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
              We're on a mission to make high-quality education accessible to everyone, everywhere.
            </p>
            <Link
              to="/courses"
              className="inline-block px-8 py-4 bg-primary-600 text-white font-medium text-lg rounded-lg shadow-lg hover:bg-primary-700 transition-transform transform hover:scale-105"
            >
              Explore Our Courses
            </Link>
          </div>
        </section>

        {/* Our Story Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-neutral-900 dark:text-white mb-6">Our Story</h2>
                <p className="text-lg text-neutral-600 dark:text-neutral-400 mb-4">
                  BoleumLearn was founded in 2021 by a team of passionate educators and technologists who believed that everyone deserves access to high-quality education, regardless of their background or location.
                </p>
                <p className="text-lg text-neutral-600 dark:text-neutral-400 mb-4">
                  What started as a small project to help friends and family learn new skills has grown into a global platform with thousands of students and hundreds of expert instructors.
                </p>
                <p className="text-lg text-neutral-600 dark:text-neutral-400 mb-6">
                  Our mission is simple: to empower individuals to achieve their full potential through education. We believe that learning should be engaging, accessible, and affordable for everyone.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link
                    to="/contact"
                    className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-900 transition-all duration-200"
                  >
                    Contact Us
                  </Link>
                </div>
              </div>
              <div className="relative">
                <img
                  src={aboutBg}
                  alt="BoleumLearn Team"
                  className="rounded-lg shadow-lg"
                />
                <div className="absolute -bottom-6 -right-6 bg-white dark:bg-neutral-800 p-6 rounded-lg shadow-lg">
                  <div className="text-3xl font-bold text-primary-600 dark:text-primary-400 mb-2">2021</div>
                  <div className="text-sm text-neutral-600 dark:text-neutral-400">Founded</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Values Section */}
        <section className="py-16 bg-neutral-50 dark:bg-neutral-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-neutral-900 dark:text-white mb-4">Our Mission & Values</h2>
              <p className="text-xl text-neutral-600 dark:text-neutral-400">What drives us every day</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm p-8">
                <h3 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-4">Our Mission</h3>
                <p className="text-lg text-neutral-600 dark:text-neutral-400 mb-4">
                  To democratize education by providing accessible, affordable, and high-quality learning experiences that empower individuals to achieve their personal and professional goals.
                </p>
                <p className="text-lg text-neutral-600 dark:text-neutral-400">
                  We believe that education is the key to unlocking human potential, and we're committed to breaking down the barriers that prevent people from accessing quality learning opportunities.
                </p>
              </div>
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm p-8">
                <h3 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-4">Our Values</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <span className="text-2xl mr-3">🌟</span>
                    <div>
                      <h4 className="font-medium text-neutral-900 dark:text-white">Excellence</h4>
                      <p className="text-neutral-600 dark:text-neutral-400">We strive for excellence in everything we do, from course content to student support.</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="text-2xl mr-3">🤝</span>
                    <div>
                      <h4 className="font-medium text-neutral-900 dark:text-white">Community</h4>
                      <p className="text-neutral-600 dark:text-neutral-400">We foster a supportive community where learners and instructors can connect and grow together.</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="text-2xl mr-3">🚀</span>
                    <div>
                      <h4 className="font-medium text-neutral-900 dark:text-white">Innovation</h4>
                      <p className="text-neutral-600 dark:text-neutral-400">We continuously innovate to provide the best learning experience using the latest technologies.</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="text-2xl mr-3">🌍</span>
                    <div>
                      <h4 className="font-medium text-neutral-900 dark:text-white">Accessibility</h4>
                      <p className="text-neutral-600 dark:text-neutral-400">We make education accessible to everyone, regardless of their background or location.</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Achievements Section */}
        <section className="py-16 bg-neutral-50 dark:bg-neutral-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-neutral-900 dark:text-white mb-4">Our Achievements</h2>
              <p className="text-xl text-neutral-600 dark:text-neutral-400">By the numbers</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {achievements.map((achievement, index) => (
                <div key={index} className="text-center">
                  <div className="text-5xl font-bold text-primary-600 dark:text-primary-400 mb-2">{achievement.number}</div>
                  <div className="text-xl font-medium text-neutral-900 dark:text-white">{achievement.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-neutral-900 dark:text-white mb-4">Why Choose BoleumLearn?</h2>
              <p className="text-xl text-neutral-600 dark:text-neutral-400">What sets us apart from other learning platforms</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {whyChooseUs.map((item, index) => (
                <div key={index} className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm p-6 hover:shadow-md transition">
                  <div className="text-4xl mb-4">{item.icon}</div>
                  <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-3">{item.title}</h3>
                  <p className="text-neutral-600 dark:text-neutral-400">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="py-16 bg-gradient-to-r from-primary-500 to-primary-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Join Our Learning Community Today</h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Whether you're looking to advance your career, learn a new skill, or simply expand your knowledge, BoleumLearn has something for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="px-8 py-4 bg-white text-primary-600 font-medium text-lg rounded-lg shadow-lg hover:bg-neutral-100 transition-transform transform hover:scale-105"
              >
                Register Now
              </Link>
              <Link
                to="/courses"
                className="px-8 py-4 bg-transparent border-2 border-white text-white font-medium text-lg rounded-lg hover:bg-white hover:text-primary-600 transition-transform transform hover:scale-105"
              >
                Browse Courses
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AboutPage;